import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shared/ui/components/shadcn/button";
import { cn } from "@shared/ui/utils/tailwind";
import { t_help } from "@i18n/usualTexts/t_help";

interface ActionButtonProps {
  text?: string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger";
  className?: string;
  id?: string;
  children?: React.ReactNode;
  /** When true, defaults to type="submit" with i18n "Next" label and submit-button styling. */
  submit?: boolean;
}

const VARIANT_MAP: Record<string, "default" | "secondary" | "destructive"> = {
  primary: "default",
  secondary: "secondary",
  danger: "destructive",
};

function resolveLabel(children: React.ReactNode, text: string | undefined, fallback?: string): React.ReactNode {
  if (children != null) return children;
  if (text != null) return text;
  return fallback;
}

function resolveDisabled(disabled: boolean, isLoading: boolean): boolean {
  return disabled || isLoading;
}

const SUBMIT_STYLE = "h-10 font-normal tracking-wide text-[13px] transition-all duration-200";

function resolveType(type: ActionButtonProps["type"], submit: boolean): "button" | "submit" | "reset" {
  return type ?? (submit ? "submit" : "button");
}

function resolveFallbackLabel(submit: boolean, nextLabel: string): string | undefined {
  return submit ? nextLabel : undefined;
}

function resolveButtonClassName(submit: boolean, className: string | undefined): string {
  return cn("w-full", submit && SUBMIT_STYLE, className);
}

const ActionButton: React.FC<ActionButtonProps> = ({ text, type, disabled = false, isLoading = false, onClick, variant = "primary", className, id, children, submit = false }) => {
  const { t } = useTranslation();
  const resolvedType = resolveType(type, submit);
  const label = resolveLabel(children, text, resolveFallbackLabel(submit, t(t_help.nextButton)));
  const isDisabled = resolveDisabled(disabled, isLoading);
  const buttonClassName = resolveButtonClassName(submit, className);
  return (
    <Button type={resolvedType} disabled={isDisabled} onClick={onClick} id={id} variant={VARIANT_MAP[variant]} className={buttonClassName} size="lg">
      {label}
    </Button>
  );
};

export default ActionButton;
